import { useEffect, useState } from 'react';
import { Button, VStack, Input } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import api from '@/utils/api';
import { FieldOption, DailogProps } from '@/lib/types';

interface Props extends DailogProps {
  option: FieldOption;
  fieldId: number;
  onSuccess(): void;
}

const formSchema = z.object({
  option_value: z.string().min(1, 'Option value is required'),
  option_label: z.string().min(1, 'Option label is required'),
  is_default: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function EditOptionModal({ isOpen, onClose, option, fieldId, onSuccess }: Props) {
  const [isCheckingUnique, setIsCheckingUnique] = useState<boolean>(false);
  const [valueExists, setValueExists] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      option_value: option?.option_value || '',
      option_label: option?.option_label || '',
      is_default: option?.is_default || false,
    },
  });

  const checkValueUniqueness = async (value: string) => {
    // Skip check if value is unchanged
    if (value === option.option_value) {
      setValueExists(false);
      return true;
    }

    setIsCheckingUnique(true);
    try {
      const response = await api.get(`/fields/${fieldId}/options`);

      const isUnique = !response.data.data.find((item: FieldOption) => item.option_value === value);
      setValueExists(!isUnique);
      return isUnique;
    } catch (err) {
      console.error('Failed to check uniqueness:', err);
      return false;
    } finally {
      setIsCheckingUnique(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const isUnique = await checkValueUniqueness(data.option_value);

      if (!isUnique) {
        setError('option_value', {
          type: 'manual',
          message: 'This option value already exists. Please use a unique value.',
        });
        return;
      }

      await api.put(`/field-options/${option.id}`, {
        option_value: data.option_value,
        option_label: data.option_label,
        is_default: data.is_default,
      });
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change role',
      });
    }
  };

  useEffect(() => {
    if (isOpen && option) {
      reset({
        option_value: option.option_value,
        option_label: option.option_label,
        is_default: option.is_default,
      });

      setValueExists(false);
    }
  }, [isOpen, option, reset]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Edit Option</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form id={'editOption'} onSubmit={handleSubmit(onSubmit)}>
            <VStack align={'stretch'} gap={4}>
              <Field
                label='Label'
                invalid={!!errors.option_label}
                errorText={errors.option_label?.message}
              >
                <Input {...register('option_label')} type='text' />
              </Field>
              <Field
                label='Value'
                invalid={!!errors.option_value}
                errorText={
                  errors.option_value?.message ||
                  (valueExists ? 'This option value already exists' : undefined)
                }
              >
                <Input {...register('option_value')} type='text' />
              </Field>
            </VStack>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button colorPalette='brand' variant={'ghost'} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette='brand'
            form={'editOption'}
            type={'submit'}
            loading={isSubmitting || isCheckingUnique}
            loadingText='Loading...'
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
